import { useState, useEffect, useCallback, Fragment } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { TH, makeSticky } from '../components/ui/gridTable';
import useSirokyDisplej from '../hooks/useSirokyDisplej';
import LayerHeader from '../components/ui/LayerHeader';
import { fmt, num, fmtKc, pct1 } from '../lib/formatters';
import { ACCENT, KANAL_BARVA, materialBarvy, tableSurfaces } from '../lib/uiTokens';
import { AopTable, BtnUlozit, LayerSpinner } from '../components/ui/aopUi';

const KANAL_COLOR = KANAL_BARVA;
const katColors = materialBarvy;
const SAZBY = [12, 21];

const rocniZaklad = (r) => (r?.zaklad || []).reduce((s, v) => s + num(v), 0);
const computedKc = (r) => num(r?.procento) / 100 * rocniZaklad(r);

// ── Donut (part-to-whole) ────────────────────────────────────────────────────
function Donut({ segments, size = 168, thickness = 22, isDark, center }) {
    const total = segments.reduce((s, d) => s + d.value, 0);
    const r = (size - thickness) / 2, cx = size / 2, cy = size / 2, circ = 2 * Math.PI * r;
    let acc = 0;
    return (
        <div style={{ position: 'relative', width: size, height: size }}>
            <svg width={size} height={size}>
                <circle cx={cx} cy={cy} r={r} fill="none" strokeWidth={thickness}
                    stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'} />
                {total > 0 && segments.map((d, i) => {
                    if (d.value <= 0) return null;
                    const len = (d.value / total) * circ;
                    const seg = Math.max(len - 2, 0.5); // 2px surface gap mezi výsečemi
                    const el = (
                        <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={d.color} strokeWidth={thickness}
                            strokeDasharray={`${seg} ${circ - seg}`} strokeDashoffset={-acc}
                            transform={`rotate(-90 ${cx} ${cy})`} />
                    );
                    acc += len;
                    return el;
                })}
            </svg>
            {center && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    {center}
                </div>
            )}
        </div>
    );
}

// ── Legenda k donutu ─────────────────────────────────────────────────────────
function Legend({ items, total }) {
    return (
        <div className="space-y-1.5">
            {items.map((it) => (
                <div key={it.label} className="flex items-center gap-2 text-[13px]">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: it.color }} />
                    <span className="flex-1 min-w-0 truncate" style={{ color: 'var(--text-secondary)' }}>{it.label}</span>
                    <span className="font-mono tabular-nums font-semibold" style={{ color: 'var(--text-primary)' }}>{fmtKc(it.value)}</span>
                    <span className="font-mono tabular-nums w-12 text-right" style={{ color: 'var(--text-tertiary)' }}>
                        {total > 0 ? pct1(it.value / total * 100) : '—'}
                    </span>
                </div>
            ))}
        </div>
    );
}

// ── Tenký split-bar (rozpad v kartě kanálu) ──────────────────────────────────
function SplitBar({ parts, isDark }) {
    const total = parts.reduce((s, p) => s + p.value, 0);
    return (
        <div className="flex h-1.5 rounded-full overflow-hidden gap-[2px]"
            style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }}>
            {total > 0 && parts.map((p, i) => (p.value > 0
                ? <div key={i} style={{ flex: p.value, background: p.color }} /> : null))}
        </div>
    );
}

export default function FinanceAopMaterial({ rok, variantaId, cardStyle, isDark }) {
    const [rows, setRows] = useState([]);
    const [kanaly, setKanaly] = useState({});
    const [kategorie, setKategorie] = useState({});
    const [trzby, setTrzby] = useState({});
    const [dirty, setDirty] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // Kanály jsou po načtení rozbalené — je jich pár a jde o hlavní pracovní plochu
    const [otevrene, setOtevrene] = useState(() => new Set());
    const siroky = useSirokyDisplej();

    const toggle = (kanal) => setOtevrene(prev => {
        const n = new Set(prev);
        n.has(kanal) ? n.delete(kanal) : n.add(kanal);
        return n;
    });

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const headers = { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token };
    const qVar = variantaId ? `&varianta_id=${variantaId}` : '';
    const KC = katColors(isDark);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const d = await fetch(`/api/finance/aop/material?rok=${rok}${qVar}`).then(r => r.json());
            setRows(d.radky || []);
            setKanaly(d.kanaly || {});
            setKategorie(d.kategorie || {});
            setTrzby(d.trzby || {});
            setDirty(new Set());
            setOtevrene(new Set(Object.keys(d.kanaly || {})));
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [rok, variantaId]);

    useEffect(() => { loadData(); }, [loadData]);

    const setProcento = (id, value) => {
        setRows(prev => prev.map(r => (r.id === id ? { ...r, procento: value } : r)));
        setDirty(prev => new Set(prev).add(id));
    };

    const handleUlozit = async () => {
        setSaving(true);
        try {
            await Promise.all([...dirty].map(id => {
                const r = rows.find(x => x.id === id);
                return r ? fetch(`/api/finance/aop/material/${id}`, {
                    method: 'PUT', headers, body: JSON.stringify({ procento: num(r.procento) }),
                }) : null;
            }));
            const res = await fetch('/api/finance/aop/material/prepocitat', {
                method: 'POST', headers, body: JSON.stringify({ rok, varianta_id: variantaId }),
            }).then(r => r.json());
            setRows(res.radky || []);
            setDirty(new Set());
        } catch (e) { alert('Chyba při ukládání'); }
        setSaving(false);
    };

    if (loading) {
        return <LayerSpinner />;
    }

    const rowFor = (kanal, kat, dph) => rows.find(r => r.kanal_kod === kanal && r.kategorie === kat && Number(r.dph_sazba) === dph);
    const trzbyKanal = (kanal, dph) => (trzby[kanal]?.[String(dph)] || []).reduce((s, v) => s + num(v), 0);
    const katKlice = Object.keys(kategorie);

    // Součty
    const katTotal = Object.fromEntries(katKlice.map(k => [k, rows.filter(r => r.kategorie === k).reduce((s, r) => s + computedKc(r), 0)]));
    const kanalTotal = Object.fromEntries(Object.keys(kanaly).map(kn => [kn, rows.filter(r => r.kanal_kod === kn).reduce((s, r) => s + computedKc(r), 0)]));
    const materialCelkem = Object.values(katTotal).reduce((a, b) => a + b, 0);
    const trzbyCelkem = Object.keys(kanaly).reduce((s, kn) => s + trzbyKanal(kn, 12) + trzbyKanal(kn, 21), 0);

    const inp = 'bg-transparent text-right text-[13px] rounded px-1.5 py-1 outline-none tabular-nums focus:ring-1 focus:ring-blue-500/60 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none';
    const { headBg, groupBg: skupinaBg, stickyBg: radekBg } = tableSurfaces(isDark);
    const th = TH;

    // Řádková tabulka přes celou šířku: název drží levý kraj, čísla vpravo.
    const colW = { nazev: 300, trzby: 168, proc: 104, kc: 140, suma: 148, podil: 104 };
    const LEFT = { nazev: 0 };
    const sticky = makeSticky(LEFT, 'nazev', siroky ? null : ['nazev']);

    const katSegments = katKlice.map(k => ({ label: kategorie[k], value: katTotal[k], color: KC[k] }));
    const kanalSegments = Object.entries(kanaly).map(([kn, nz]) => ({ label: nz, value: kanalTotal[kn], color: KANAL_COLOR[kn] || '#888' }));

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {/* Hlavička vrstvy */}
            <LayerHeader krok={2} title="Materiálové náklady (rozpad)" cardStyle={cardStyle}>
                U každé kategorie zadáš <b>% z celkových tržeb kanálu</b> (mezisoučtu). Sloupce 12 % / 21 % = <b>v jaké sazbě se věc nakupuje</b>
                {' '}(DPH na vstupu). Přesný rozpad Suroviny / Zboží / Obaly jde do Výsledovky. „Uložit" přepočítá měsíce z tržeb.
            </LayerHeader>

            {/* Toolbar */}
            <div className="flex items-center gap-2 mb-4 flex-wrap flex-shrink-0">
                <div className="flex-1" />
                <div className="text-sm mr-2" style={{ color: 'var(--text-tertiary)' }}>
                    Materiál celkem: <b style={{ color: 'var(--text-primary)' }}>{fmtKc(materialCelkem)}</b>
                    {trzbyCelkem > 0 && <span className="ml-1">({pct1(materialCelkem / trzbyCelkem * 100)} tržeb)</span>}
                </div>
                <BtnUlozit onClick={handleUlozit} saving={saving} dirtyCount={dirty.size} />
            </div>

            {/* Řádkový rozpad přes celou šířku — sekce = kanál, řádky = kategorie */}
            <AopTable cardStyle={cardStyle}>
                <colgroup>
                    <col style={{ width: colW.nazev }} />
                    <col style={{ width: colW.trzby }} />
                    <col style={{ width: colW.proc }} />
                    <col style={{ width: colW.kc }} />
                    <col style={{ width: colW.proc }} />
                    <col style={{ width: colW.kc }} />
                    <col style={{ width: colW.suma }} />
                    <col style={{ width: colW.podil }} />
                </colgroup>
                <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                        <th className={`${th} text-left`} style={{ ...sticky('nazev', 30, headBg), color: 'var(--text-secondary)' }}>Kanál / kategorie</th>
                        <th className={`${th} text-right`} style={{ background: headBg, color: 'var(--text-tertiary)' }}>Tržby</th>
                        <th className={`${th} text-right`} style={{ background: headBg, color: 'var(--text-tertiary)' }}>% 12 %</th>
                        <th className={`${th} text-right`} style={{ background: headBg, color: 'var(--text-tertiary)' }}>Nákup 12 %</th>
                        <th className={`${th} text-right`} style={{ background: headBg, color: 'var(--text-tertiary)' }}>% 21 %</th>
                        <th className={`${th} text-right`} style={{ background: headBg, color: 'var(--text-tertiary)' }}>Nákup 21 %</th>
                        <th className={`${th} text-right`} style={{ background: headBg, color: ACCENT }}>Materiál Σ</th>
                        <th className={`${th} text-right`} style={{ background: headBg, color: 'var(--text-tertiary)' }}>% tržeb</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(kanaly).map(([kanal, nazev]) => {
                        const kColor = KANAL_COLOR[kanal] || 'var(--text-tertiary)';
                        const t12 = trzbyKanal(kanal, 12), t21 = trzbyKanal(kanal, 21), tCelk = t12 + t21;
                        const matKanal = kanalTotal[kanal] || 0;
                        const otevreno = otevrene.has(kanal);
                        const Chevron = otevreno ? ChevronDown : ChevronRight;
                        const splitParts = katKlice.map(k => ({ value: SAZBY.reduce((s, dph) => s + computedKc(rowFor(kanal, k, dph)), 0), color: KC[k] }));
                        return (
                            <Fragment key={kanal}>
                                {/* Kanál — souhrn, klik rozbalí kategorie */}
                                <tr onClick={() => toggle(kanal)} className="cursor-pointer"
                                    style={{ background: skupinaBg, borderTop: '2px solid var(--border-default)' }}>
                                    <td className="px-3 py-2.5" style={sticky('nazev', 10, skupinaBg)}>
                                        <div className="flex items-center gap-2">
                                            <Chevron size={14} style={{ color: kColor, flexShrink: 0 }} />
                                            <span className="text-[13px] font-bold" style={{ color: kColor }}>{nazev}</span>
                                        </div>
                                        {/* Poměr kategorií v kanálu — rychlý přehled bez rozbalení */}
                                        <div className="mt-1.5" style={{ paddingLeft: 22 }}><SplitBar parts={splitParts} isDark={isDark} /></div>
                                    </td>
                                    <td className="px-3 text-right text-[12.5px] font-mono tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                                        {fmtKc(tCelk)}
                                        <div className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>12 %: {fmt(t12)} · 21 %: {fmt(t21)}</div>
                                    </td>
                                    <td /><td /><td /><td />
                                    <td className="px-3 text-right text-[13px] font-mono font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>{fmtKc(matKanal)}</td>
                                    <td className="px-3 text-right text-[12.5px] font-mono tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
                                        {tCelk > 0 ? pct1(matKanal / tCelk * 100) : '—'}
                                    </td>
                                </tr>

                                {/* Kategorie kanálu */}
                                {otevreno && katKlice.map(kat => {
                                    const r12 = rowFor(kanal, kat, 12), r21 = rowFor(kanal, kat, 21);
                                    const sum = computedKc(r12) + computedKc(r21);
                                    return (
                                        <tr key={kat} style={{ borderBottom: '1px solid var(--border-subtle)', background: radekBg }}>
                                            <td className="py-2 pr-3 text-[13px]" style={{ ...sticky('nazev', 10, radekBg), paddingLeft: 34, color: 'var(--text-secondary)' }}>
                                                <span className="inline-flex items-center gap-2">
                                                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: KC[kat] }} />
                                                    {kategorie[kat]}
                                                </span>
                                            </td>
                                            <td />
                                            {[[r12, 12], [r21, 21]].map(([r, dph]) => (
                                                <Fragment key={dph}>
                                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                                        <input type="text" inputMode="decimal" value={r?.procento ?? ''} placeholder="0"
                                                            onChange={e => setProcento(r.id, e.target.value.replace(/[^\d.]/g, ''))}
                                                            className={inp} style={{ width: 52, color: 'var(--text-primary)', border: '1px solid var(--border-default)' }} />
                                                        <span className="text-[11px] ml-1" style={{ color: 'var(--text-tertiary)' }}>%</span>
                                                    </td>
                                                    <td className="px-3 text-right text-[12.5px] font-mono tabular-nums" style={{ color: 'var(--text-tertiary)' }}>{fmtKc(computedKc(r))}</td>
                                                </Fragment>
                                            ))}
                                            <td className="px-3 text-right text-[12.5px] font-mono font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{fmtKc(sum)}</td>
                                            <td className="px-3 text-right text-[12.5px] font-mono tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
                                                {tCelk > 0 ? pct1(sum / tCelk * 100) : '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </Fragment>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr style={{ borderTop: '2px solid var(--border-default)', background: headBg }}>
                        <td className="px-3 py-2.5 text-[13px] font-bold whitespace-nowrap" style={{ ...sticky('nazev', 20, headBg), color: 'var(--text-primary)' }}>Materiál celkem</td>
                        <td className="px-3 text-right text-[12.5px] font-mono tabular-nums" style={{ color: 'var(--text-secondary)' }}>{fmtKc(trzbyCelkem)}</td>
                        <td /><td /><td /><td />
                        <td className="px-3 text-right text-[13px] font-mono font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>{fmtKc(materialCelkem)}</td>
                        <td className="px-3 text-right text-[12.5px] font-mono tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
                            {trzbyCelkem > 0 ? pct1(materialCelkem / trzbyCelkem * 100) : '—'}
                        </td>
                    </tr>
                </tfoot>
            </AopTable>

            {/* Souhrn s grafy */}
            <div className="rounded-2xl p-5 mt-3 flex-shrink-0" style={cardStyle}>
                <h3 className="text-xs font-bold uppercase tracking-wide mb-0.5" style={{ color: 'var(--text-secondary)' }}>Rozpad materiálu celkem</h3>
                <div className="text-[11px] mb-4" style={{ color: 'var(--text-tertiary)' }}>Přesný rozpad per kategorie jde do Výsledovky jako plán přímých nákladů.</div>
                <div className="flex flex-wrap gap-x-10 gap-y-5">
                    {/* Podle kategorie */}
                    <div className="flex items-center gap-4">
                        <Donut segments={katSegments} isDark={isDark} size={128} thickness={18} center={
                            <>
                                <span className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Materiál</span>
                                <span className="text-[14px] font-bold font-mono tabular-nums" style={{ color: 'var(--text-primary)' }}>{fmt(materialCelkem)}</span>
                                <span className="text-[9px]" style={{ color: 'var(--text-tertiary)' }}>Kč</span>
                            </>
                        } />
                        <div style={{ minWidth: 240 }}>
                            <div className="text-[10px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-tertiary)' }}>Podle kategorie</div>
                            <Legend items={katSegments} total={materialCelkem} />
                        </div>
                    </div>
                    {/* Podle kanálu */}
                    <div className="flex items-center gap-4">
                        <Donut segments={kanalSegments} isDark={isDark} size={128} thickness={18} center={
                            <>
                                <span className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Kanály</span>
                                <span className="text-[15px] font-bold font-mono tabular-nums" style={{ color: 'var(--text-primary)' }}>{Object.keys(kanaly).length}</span>
                            </>
                        } />
                        <div style={{ minWidth: 260 }}>
                            <div className="text-[10px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-tertiary)' }}>Podle kanálu</div>
                            <Legend items={kanalSegments} total={materialCelkem} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
